/**
 * Inline SVG playing card. Mirrors iOS CardView spec:
 *  - Ivory card stock with subtle gradient + drop shadow
 *  - Big centered suit symbol — the dominant visual at ~50% of card height
 *  - Rank + small suit in BOTH the top-left and bottom-right corners
 *    (bottom-right rotated 180° as on real playing cards)
 *  - Deep crimson (#c8113b) for hearts/diamonds, slate (#15191e) for clubs/spades
 *  - Center suit at 0.85 opacity so corner marks read clearly against it
 *
 * Two sizes: sm (board cards) ~44×62, md (hero hand) ~54×76 — same ratios
 * as iOS .board / .hero (close to a real playing card's ~1:1.4).
 */

interface PlayingCardProps {
  /** Wire format like "Ah", "Td", "10s" — parsed permissively. */
  token: string;
  size?: "sm" | "md" | "lg";
}

interface CardDims {
  w: number;
  h: number;
  rankFont: number;
  cornerSuitFont: number;
  centerSuitFont: number;
  radius: number;
  cornerInset: number;
}

const SIZES: Record<NonNullable<PlayingCardProps["size"]>, CardDims> = {
  sm: { w: 44, h: 62, rankFont: 13, cornerSuitFont: 10, centerSuitFont: 28, radius: 5, cornerInset: 4 },
  md: { w: 54, h: 76, rankFont: 16, cornerSuitFont: 12, centerSuitFont: 36, radius: 6, cornerInset: 5 },
  lg: { w: 72, h: 100, rankFont: 21, cornerSuitFont: 15, centerSuitFont: 50, radius: 8, cornerInset: 6 },
};

export function PlayingCard({ token, size = "sm" }: PlayingCardProps) {
  const parsed = parseToken(token);
  const dims = SIZES[size];

  if (!parsed) {
    return (
      <span
        className="inline-block rounded font-mono text-xs px-1.5 py-0.5 bg-white/5 text-text-muted"
        style={{ minWidth: dims.w, minHeight: dims.h }}
      >
        {token}
      </span>
    );
  }

  const { rank, suit, isRed } = parsed;
  const ink = isRed ? "#c8113b" : "#15191e";

  return (
    <span
      className="inline-block relative select-none"
      style={{
        width: dims.w,
        height: dims.h,
        filter: "drop-shadow(0 2px 3px rgba(0, 0, 0, 0.5))",
      }}
      aria-label={`${rank} of ${suitWordFromGlyph(suit)}`}
    >
      <svg
        viewBox={`0 0 ${dims.w} ${dims.h}`}
        width={dims.w}
        height={dims.h}
        style={{ display: "block" }}
      >
        <defs>
          <linearGradient id={`pc-bg-${size}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#fdfbf6" />
            <stop offset="100%" stopColor="#eeeae0" />
          </linearGradient>
        </defs>

        {/* Card stock */}
        <rect
          x="0.5"
          y="0.5"
          width={dims.w - 1}
          height={dims.h - 1}
          rx={dims.radius}
          fill={`url(#pc-bg-${size})`}
          stroke="rgba(0, 0, 0, 0.18)"
          strokeWidth="1"
        />

        {/* Centered big suit — the dominant visual. Soft-opacity so corner
         *  marks read cleanly against it. */}
        <text
          x={dims.w / 2}
          y={dims.h / 2}
          fontSize={dims.centerSuitFont}
          fontFamily="-apple-system, 'SF Pro Text', 'Segoe UI Symbol', system-ui, sans-serif"
          fill={ink}
          opacity={0.86}
          textAnchor="middle"
          dominantBaseline="central"
        >
          {suit}
        </text>

        {/* Top-left corner mark — rank above, small suit below */}
        <CornerMark
          x={dims.cornerInset}
          y={dims.cornerInset}
          rank={rank}
          suit={suit}
          ink={ink}
          dims={dims}
        />

        {/* Bottom-right corner mark — rotated 180° around the corner anchor */}
        <g
          transform={`rotate(180 ${dims.w - dims.cornerInset} ${dims.h - dims.cornerInset})`}
        >
          <CornerMark
            x={dims.w - dims.cornerInset}
            y={dims.h - dims.cornerInset}
            rank={rank}
            suit={suit}
            ink={ink}
            dims={dims}
          />
        </g>
      </svg>
    </span>
  );
}

interface CornerMarkProps {
  x: number;
  y: number;
  rank: string;
  suit: string;
  ink: string;
  dims: CardDims;
}

/** Stacked rank-above-suit. `x`/`y` is the top-left anchor where the rank
 *  baseline sits. */
function CornerMark({ x, y, rank, suit, ink, dims }: CornerMarkProps) {
  // Rank baseline placed one rank-line below the anchor; suit follows.
  const rankBaselineY = y + dims.rankFont;
  const suitBaselineY = rankBaselineY + dims.cornerSuitFont * 0.95;
  return (
    <>
      <text
        x={x}
        y={rankBaselineY}
        fontSize={dims.rankFont}
        fontWeight="700"
        fontFamily="-apple-system, 'SF Pro Text', system-ui, sans-serif"
        fill={ink}
        textAnchor="start"
      >
        {rank}
      </text>
      <text
        x={x}
        y={suitBaselineY}
        fontSize={dims.cornerSuitFont}
        fontFamily="-apple-system, 'SF Pro Text', 'Segoe UI Symbol', system-ui, sans-serif"
        fill={ink}
        textAnchor="start"
      >
        {suit}
      </text>
    </>
  );
}

function parseToken(
  token: string,
): { rank: string; suit: string; isRed: boolean } | null {
  if (!token) return null;
  const t = token.trim();
  let rankPart: string;
  let suitPart: string;
  if (t.length === 3 && t.slice(0, 2).toLowerCase() === "10") {
    rankPart = "10";
    suitPart = t.slice(2).toLowerCase();
  } else if (t.length === 2) {
    rankPart = t[0]!.toUpperCase();
    suitPart = t[1]!.toLowerCase();
  } else {
    return null;
  }
  const suitGlyph = { c: "♣", d: "♦", h: "♥", s: "♠" }[suitPart];
  if (!suitGlyph) return null;
  return {
    rank: rankPart,
    suit: suitGlyph,
    isRed: suitPart === "h" || suitPart === "d",
  };
}

function suitWordFromGlyph(g: string): string {
  return { "♣": "clubs", "♦": "diamonds", "♥": "hearts", "♠": "spades" }[g] ?? "?";
}
